import type { CoinType } from '../cointype/CoinTypeStore';

import type { BattleLeaderboard, BattleLeaderboardPage, BattleLeaderboardRow, BattleQualification } from './types';

/** A percentage held to 0–100, whatever core sent. */
export const clampPct = (value: number) => Math.min(100, Math.max(0, value));

export type BattleLeaderboardDisplay = {
  /** The page as core sent it. The reader's own line stays in its place, to be highlighted there. */
  rows: BattleLeaderboardRow[];
  /** Whose line is the reader's, in `rows` or `pinnedRow`. */
  playerId: number;
  /**
   * The reader's line when they are ranked but not on this page, pinned below the list. Null when the
   * reader is already in `rows`, or unranked (they never played the leading slot).
   */
  pinnedRow: BattleLeaderboardRow | null;
  hasMore: boolean;
};

const isRow = (row: unknown): row is BattleLeaderboardRow =>
  typeof (row as BattleLeaderboardRow | null)?.playerId === 'number';

/**
 * player-backend's leaderboard page, flat next to `success` and `message`. `locked` and `not_found` (the
 * battle is gone) end paging. Null for an error body or a shape this client does not know: a failed read,
 * which the caller retries later.
 */
export function parseLeaderboardPage(response: unknown): BattleLeaderboardPage | null {
  const body = (response as { data?: Record<string, unknown> } | undefined)?.data;

  if (body?.status === 'locked' || body?.status === 'not_found') {
    return { status: body.status };
  }

  if (!body || body.status !== 'unlocked' || !Array.isArray(body.rows)) {
    return null;
  }

  return {
    status: 'unlocked',
    offset: Number(body.offset) || 0,
    limit: Number(body.limit) || 0,
    leadingGameId: Number(body.leadingGameId) || 0,
    rows: body.rows.filter(isRow),
    player: isRow(body.player) ? body.player : null,
    hasMore: body.hasMore === true,
    total: Number(body.total) || 0,
  };
}

/**
 * The first page (from the battle read, refreshed every few seconds) followed by the pages read on
 * scroll. A later page was read at another moment, so a player can show up on both: the first page wins,
 * and a later row is kept only below its last position. Never past X (`maxPosition`).
 */
export function mergeLeaderboardRows(
  firstPage: BattleLeaderboardRow[],
  laterRows: BattleLeaderboardRow[],
  maxPosition: number,
): BattleLeaderboardRow[] {
  const seen = new Set(firstPage.map((row) => row.playerId));
  const lastPosition = firstPage.reduce((last, row) => Math.max(last, row.position ?? 0), 0);
  const merged = [...firstPage];

  for (const row of laterRows) {
    if (!seen.has(row.playerId) && row.position !== null && row.position > lastPosition) {
      seen.add(row.playerId);
      merged.push(row);
    }
  }

  return merged.filter((row) => !(maxPosition > 0) || row.position === null || row.position <= maxPosition);
}

/**
 * The leaderboard lists only players of the leading slot. The reader's line stays in its place on the
 * page; when they are ranked off the page it is pinned below, as the tournament leaderboard does. A
 * reader who never played the leading slot (`position: null`), or with no leaderboard yet, is not on
 * the list at all: an unranked "-" line read as a broken rank (GS-813).
 */
export function buildLeaderboardView(
  leaderboard: BattleLeaderboard | null,
  playerId: number | null,
  fallbackName: string,
): BattleLeaderboardDisplay {
  const fromPayload = leaderboard?.player ?? null;
  const ownId = fromPayload?.playerId ?? playerId ?? 0;

  // core sends the reader's display name, which is empty for a player with no name on file.
  const asOwnRow = (row: BattleLeaderboardRow): BattleLeaderboardRow => ({
    ...row,
    displayName: row.displayName?.trim() ? row.displayName : fallbackName,
    sharePct: row.sharePct ?? 0,
  });

  const rows = (leaderboard?.rows ?? []).map((row) => (row.playerId === ownId ? asOwnRow(row) : row));
  const isOnPage = rows.some((row) => row.playerId === ownId);

  return {
    rows,
    playerId: ownId,
    pinnedRow: !isOnPage && fromPayload?.position != null ? asOwnRow(fromPayload) : null,
    hasMore: leaderboard?.hasMore ?? false,
  };
}

/**
 * How full the qualify bar is, 0–100. A zero or negative threshold means there is nothing to reach,
 * so the bar reads full; progress past the threshold never overflows it.
 */
export function qualificationPct(qualification: BattleQualification): number {
  const { threshold, progress } = qualification;

  if (!(threshold > 0)) {
    return 100;
  }

  return Math.min(100, Math.max(0, (progress / threshold) * 100));
}

/** How much more the reader has to spin, in the run's coin, to unlock the leaderboard. Never below 0. */
export function unlockRemaining(qualification: BattleQualification): number {
  return Math.max(qualification.threshold - Math.max(qualification.progress, 0), 0);
}

/** What the lock bar's text reads: `progress/threshold` in the run's coin. */
export type QualificationDisplay = {
  progress: number;
  threshold: number;
  qualified: boolean;
};

/**
 * The smallest step `formatAmountByCoinType` shows at this size. SC goes through `toLocaleString()`,
 * which keeps three decimals. GC goes through `formatValueWithK` in round mode: two decimals below
 * 1K, then one decimal of K (100 GC), two decimals of M (10K GC) and two decimals of B (10M GC).
 */
function displayStep(amount: number, coinType: CoinType): number {
  if (coinType !== 'GC') {
    return 0.001;
  }
  if (amount >= 1_000_000_000) {
    return 10_000_000;
  }
  if (amount >= 1_000_000) {
    return 10_000;
  }
  if (amount >= 1_000) {
    return 100;
  }
  return 0.01;
}

/** `value` floored to a whole number of `step`s, without float noise (1.13 stays 1.13). */
function floorToStep(value: number, step: number, below?: number): number {
  let units = Math.floor(Number((value / step).toFixed(6)));
  if (below !== undefined && units * step >= below) {
    units -= 1;
  }
  return step < 1 ? units / Math.round(1 / step) : units * step;
}

/**
 * The numbers the qualify bar prints. Progress never reads past the threshold, so 5,200 of 3,500
 * reads 3,500/3,500. Short of the threshold it is floored to what the formatter shows, so a
 * formatter that rounds (3,499 GC as "3.5K") cannot read as qualified before the player is.
 * A zero or negative threshold means there is nothing to reach. Scoring is not touched.
 */
export function qualificationDisplay(qualification: BattleQualification, coinType: CoinType): QualificationDisplay {
  const threshold = Math.max(qualification.threshold, 0);
  const qualified = !(qualification.threshold > 0) || qualification.progress >= qualification.threshold;
  const progress = Math.min(Math.max(qualification.progress, 0), threshold);

  if (qualified) {
    return { progress, threshold, qualified };
  }

  return {
    progress: Math.max(floorToStep(progress, displayStep(progress, coinType), threshold), 0),
    threshold,
    qualified,
  };
}
